import { useEffect, useState } from 'react';
import { fetchUserApi } from '@/api/userApi';
import useAuthStore from '@store/useAuthStore';
import useMetadataStore from '@/store/useMetadataStore';

/**
 * Hook para validar la sesión del usuario al iniciar la aplicación.
 *
 * Verifica si hay un token guardado en localStorage y lo valida contra el backend.
 * Si el token es inválido, el interceptor de axios se encargará de limpiar el estado.
 *
 * @returns {Object} - { isValidating: boolean, isValid: boolean | null }
 */
export default function useSessionValidator() {
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const { user, updateUserData, clearAuthState } = useAuthStore();

  useEffect(() => {
    const validateSession = async () => {
      // If no user in store, no need to validate
      if (!user) {
        setIsValid(false);
        return;
      }

      // Check if token exists in localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        // Token missing but user exists in store - clear invalid state
        console.warn('[useSessionValidator] Token missing but user exists in store. Clearing state.');
        clearAuthState();
        setIsValid(false);
        return;
      }

      // Token exists - validate with backend
      setIsValidating(true);
      try {
        const userData = await fetchUserApi();

        // Update user data in store with fresh data from backend
        if (userData) {
          updateUserData(userData);

          // Ensure metadata is loaded (like login/register do)
          const metadataState = useMetadataStore.getState();
          if (!metadataState.loaded && !metadataState.loading) {
            await metadataState.fetchMetadata();
          }

          setIsValid(true);
          console.info('[useSessionValidator] Session validated successfully');
        } else {
          // Unexpected: API succeeded but no user data
          console.error('[useSessionValidator] API succeeded but no user data returned');
          clearAuthState();
          setIsValid(false);
        }
      } catch (error) {
        // API call failed - interceptor will handle cleanup for 401
        // For other errors, we also clear the state to be safe
        console.error('[useSessionValidator] Session validation failed:', error);
        setIsValid(false);
        // Note: clearAuthState is called by the interceptor for 401 errors
      } finally {
        setIsValidating(false);
      }
    };

    validateSession();
  }, []); // Run only once on mount

  return { isValidating, isValid };
}
